import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiClient } from "@/lib/api-client"
import { AI_HEALTH_KEY, type AiHealth } from "@/lib/ai-health"

/**
 * Live AI provider health. Polls fast while the status is unknown or a check is running, every 10 s while
 * something is wrong (so the warning clears soon after a top-up), and every 20 s while healthy (so a failure in
 * a background job shows up quickly). Also re-checks on window focus and when the network comes back; failed
 * requests elsewhere trigger an immediate re-check (see app/query-client.ts).
 */
export function useAiHealth() {
  return useQuery({
    queryKey: AI_HEALTH_KEY,
    queryFn: async () => (await apiClient.get<AiHealth>("/ai/health")).data,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // One quick retry so a single dropped request doesn't flash the "can't reach the API" warning.
    retry: 1,
    retryDelay: 1_000,
    refetchInterval: (query) => {
      if (query.state.error) return 10_000
      const health = query.state.data
      if (!health || health.status === "unknown" || health.checking) return 4_000
      return health.severity === "ok" ? 20_000 : 10_000
    },
  })
}

/** "Check again": the server re-tests the provider now and answers with the result. */
export function useCheckAiHealth() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await apiClient.post<AiHealth>("/ai/health/check", {})).data,
    onSuccess: (health) => queryClient.setQueryData(AI_HEALTH_KEY, health),
    onError: () => queryClient.invalidateQueries({ queryKey: AI_HEALTH_KEY }),
  })
}
